import React from 'react'
import { ChevronLeft, ChevronRight } from '@mui/icons-material'
import { Typography } from '@mui/material'
import { CircleMinusMinor, CirclePlusMinor } from '@shopify/polaris-icons'
import tw from 'twin.macro'
import { Button } from './Button'
import { GSIcon } from './GSIcon'

interface FileViewerControlsProps {
  isPdf: boolean
  numPages: number
  pageNumber: number
  onPreviousPage: () => void
  onNextPage: () => void
  onZoomOut: () => void
  onZoomIn: () => void
  isZoomOutDisabled: boolean
  onRotateRight: () => void
  onRotateLeft: () => void
  partnerDriverId?: string
  stepId?: string
  documentId?: string
  filePath: string
  onDownload: () => void
  isDownloading: boolean
}

export const FileViewerControls: React.FC<FileViewerControlsProps> = ({
  isPdf,
  numPages,
  pageNumber,
  onPreviousPage,
  onNextPage,
  onZoomOut,
  onZoomIn,
  isZoomOutDisabled,
  onRotateRight,
  onRotateLeft,
  partnerDriverId,
  stepId,
  documentId,
  filePath,
  onDownload,
  isDownloading,
}) => {
  return (
    <div
      css={tw`bg-white py-2 px-4 flex flex-row items-center justify-center gap-8 self-stretch shadow-lg flex-shrink-0`}
    >
      {isPdf && numPages > 1 && (
        <div css={tw`flex items-center gap-3`}>
          <Typography variant="body2" color="black">
            Page
          </Typography>
          <div css={tw`flex gap-2 items-center`}>
            <Button
              data-testid="customer-interface-file-prev-page-btn"
              onClick={onPreviousPage}
              disabled={pageNumber <= 1}
              color="white"
              size="small"
              leftIcon={
                <div css={tw`flex items-center justify-center w-full h-full`}>
                  <ChevronLeft sx={{ color: 'black' }} />
                </div>
              }
              className={tw`w-[36px] rounded-lg !p-0`}
            />
            <Typography variant="body2" color="black" className="min-w-[60px] text-center">
              {pageNumber} / {numPages}
            </Typography>
            <Button
              data-testid="customer-interface-file-next-page-btn"
              onClick={onNextPage}
              disabled={pageNumber >= numPages}
              color="white"
              size="small"
              leftIcon={
                <div css={tw`flex items-center justify-center w-full h-full`}>
                  <ChevronRight sx={{ color: 'black' }} />
                </div>
              }
              className={tw`w-[36px] rounded-lg !p-0`}
            />
          </div>
        </div>
      )}

      <div css={tw`flex items-center gap-3`}>
        <Typography variant="body2" color="black">
          Zoom
        </Typography>
        <div css={tw`flex gap-2`}>
          <Button
            data-testid="customer-interface-file-zoom-out-btn"
            onClick={onZoomOut}
            disabled={isZoomOutDisabled}
            color="white"
            size="small"
            leftIcon={<CircleMinusMinor width={20} height={20} />}
            className={tw`w-[36px] rounded-lg !p-0`}
          />
          <Button
            data-testid="customer-interface-file-zoom-in-btn"
            onClick={onZoomIn}
            color="white"
            size="small"
            leftIcon={<CirclePlusMinor width={20} height={20} />}
            className={tw`w-[36px] rounded-lg !p-0`}
          />
        </div>
      </div>

      <div css={tw`flex items-center gap-3`}>
        <Typography variant="body2" color="black">
          Rotate
        </Typography>
        <div css={tw`flex gap-2`}>
          <Button
            data-testid="customer-interface-file-rotate-right-btn"
            onClick={onRotateRight}
            color="white"
            size="small"
            leftIcon={<GSIcon iconName="gs_icon_arrow_rotate_clockwise" width={20} height={20} />}
            className={tw`w-[36px] rounded-lg !p-0`}
          />
          <Button
            data-testid="customer-interface-file-rotate-left-btn"
            onClick={onRotateLeft}
            color="white"
            size="small"
            leftIcon={
              <GSIcon iconName="gs_icon_arrow_rotate_counter_clockwise" width={20} height={20} />
            }
            className={tw`w-[36px] rounded-lg !p-0`}
          />
        </div>
      </div>

      {(partnerDriverId && stepId && documentId) || Boolean(filePath) ? (
        <Button
          data-testid="customer-interface-file-download-btn"
          onClick={onDownload}
          disabled={isDownloading}
          loading={isDownloading}
          color="white"
          size="small"
          rightIcon={
            !isDownloading ? (
              <GSIcon iconName="gs_icon_circle_arrow_down" width={20} height={20} />
            ) : undefined
          }
          className={tw`rounded-lg`}
          title="Download"
        >
          Download
        </Button>
      ) : null}
    </div>
  )
}
